import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { navPath } from '@/constant';
import { useDebounce } from '@/hooks/use-debounce';
import { CustomPagination } from '@/pages/dashboard/components/custom-pagination';
import { format } from 'date-fns';
import { Loader2, Search, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import DateRangePicker from '../../components/date-range-picker';
import { withRoleGuard } from '../../components/with-role-guard';

interface LogEntry {
  username: string;
  type: string;
  log: string;
  ip: string;
  failed_attempt: number;
  createdAt: string;
}

const LOG_TYPES = ['LOGIN', 'LINK_UPDATED', 'LINK_ADDED', 'LINK_DELETED', 'RESET_PASSWORD', 'BLOCKIP'];

const Changelog = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [ipFilter, setIpFilter] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, typeFilter, ipFilter, dateRange]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setTypeFilter('');
    setIpFilter('');
    setDateRange(undefined);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);

    const params = new URLSearchParams();
    params.append('page', currentPage.toString());

    if (debouncedSearchQuery) params.append('search', debouncedSearchQuery);
    if (typeFilter) params.append('type', typeFilter);
    if (ipFilter) params.append('ip', ipFilter);
    if (dateRange?.from) params.append('startDate', dateRange.from.toISOString());
    if (dateRange?.to) params.append('endDate', dateRange.to.toISOString());

    try {
      const response = await fetch(`/api/v1/log/?${params.toString()}`);
      const data = await response.json();

      setLogs(data.data.all_log_data);
      setTotalLogs(data.data.total.data);
      setTotalPages(data.data.total.page);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchQuery, typeFilter, ipFilter, dateRange]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getLogTypeColor = (type: string) => {
    switch (type) {
      case 'LOGIN':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400';
      case 'LINK_ADDED':
        return 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400';
      case 'LINK_UPDATED':
        return 'bg-yellow-100 text-yellow-600 dark:bg-yellow-500/20 dark:text-yellow-400';
      case 'LINK_DELETED':
        return 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400';
      case 'RESET_PASSWORD':
        return 'bg-pink-100 text-pink-600 dark:bg-pink-500/20 dark:text-pink-400';
      case 'BLOCKIP':
        return 'bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400';
      default:
        return 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground';
    }
  };

  return (
    <div className='space-y-4'>
      <Card className='border-primary/20 shadow-sm shadow-primary/10 dark:bg-gray-900 dark:border-gray-800 dark:shadow-md'>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4 border-b border-primary/20 dark:border-gray-800'>
          <CardTitle className='text-xl font-bold'>System Changelog</CardTitle>
        </CardHeader>
        <CardContent className='pt-4 space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'>
            <div className='relative'>
              <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search logs...'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className='w-full pl-8 pr-8'
              />
              {searchQuery && (
                <Button variant='ghost' size='icon' className='absolute right-1 top-1.5 h-6 w-6' onClick={handleClearSearch}>
                  <X className='h-4 w-4' />
                </Button>
              )}
            </div>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder='Filter by type' />
              </SelectTrigger>
              <SelectContent>
                {LOG_TYPES.map(type => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input placeholder='Filter by IP' value={ipFilter} onChange={e => setIpFilter(e.target.value)} />

            <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} className='w-full' />

            <Button
              variant='outline'
              className='w-full flex items-center justify-center hover:text-destructive'
              onClick={handleClearFilters}
            >
              <X className='mr-2 h-4 w-4' />
              Clear Filters
            </Button>
          </div>

          <div className='rounded-md border border-primary/20 dark:border-gray-800 dark:bg-gray-900'>
            <Table>
              <TableHeader className='bg-primary/5 dark:bg-gray-800'>
                <TableRow className='hover:bg-primary/5 dark:hover:bg-gray-800'>
                  <TableHead className='w-[15%] font-bold text-foreground dark:text-foreground'>Username</TableHead>
                  <TableHead className='w-[15%] font-bold text-foreground dark:text-foreground'>Type</TableHead>
                  <TableHead className='w-[40%] font-bold text-foreground dark:text-foreground'>Log</TableHead>
                  <TableHead className='w-[10%] font-bold text-foreground dark:text-foreground'>IP</TableHead>
                  <TableHead className='w-[20%] text-center font-bold text-foreground dark:text-foreground'>Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className='dark:bg-gray-900'>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className='h-60'>
                      <div className='flex justify-center items-center h-full'>
                        <Loader2 className='h-8 w-8 animate-spin text-primary' />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className='text-center py-10 text-muted-foreground'>
                      No logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log, index) => (
                    <TableRow key={index}>
                      <TableCell>{log.username}</TableCell>
                      <TableCell>
                        <span className={`px-3 py-2 rounded-full ${getLogTypeColor(log.type)}`}>{log.type}</span>
                      </TableCell>
                      <TableCell>
                        {log.type === 'LOGIN' ? (
                          <div className='flex items-center'>
                            <span>{log.log}</span>
                            <span
                              className={`ml-2 px-2 py-1 rounded text-xs ${
                                log.failed_attempt === 0
                                  ? 'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400'
                                  : 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400'
                              }`}
                            >
                              {log.failed_attempt}
                            </span>
                          </div>
                        ) : (
                          log.log
                        )}
                      </TableCell>
                      <TableCell>{log.ip}</TableCell>
                      <TableCell className='text-center'>{format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm:ss')}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {!isLoading && (
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalLogs}
              onPageChange={setCurrentPage}
              itemsPerPage={10}
              showItemCount={true}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default withRoleGuard(Changelog, ['MASTER'], navPath.login);
